import {
    Flex,
    gridNestedBehavior,
    ShorthandCollection,
    Table,
    TableCellProps,
    TableRowProps,
} from "@fluentui/react-northstar";
import React, { useContext, useEffect, useState } from "react";
import styles from "./basicTable.module.scss";
import classNames from "classnames";
import pl from "shared/messages/pl";
import { IThemeTeamsClient } from "msTeamsUI/lib/withTheme";
import { MsTeamsContext } from "msTeams/store";
import { ResizerContext } from "shared/stores/resizeStore/ResizerContext";
import { constants } from "shared/constants";
import { ShowMoreButton } from "view360Card/components";

export interface BasicTableProps {
    headerItems?: ShorthandCollection<TableCellProps>;
    rows?: ShorthandCollection<TableRowProps>;
    mobileViewRows?: JSX.Element[] | null;
    maxDefaultSize?: number;
}

export const BasicTable: React.FC<BasicTableProps> = (props) => {
    const msTeamsContext = useContext(MsTeamsContext);
    const resizeContext = useContext(ResizerContext);
    const [rows, setRows] = useState<ShorthandCollection<TableRowProps> | undefined>();
    const [mobileViewRows, setMobileViewRows] = useState<JSX.Element[] | undefined | null>();

    const isMobile = resizeContext.windowWidth < constants.view360MobileDeviceWidthPx;

    useEffect(() => {
        let rowsToShow = props.rows;
        let mobileViewRows = props.mobileViewRows;
        if (isExpandable()) {
            rowsToShow = getUnexpandedRows();
            mobileViewRows = getUnexpandedMobileViewRows();
        }
        setRows(rowsToShow);
        setMobileViewRows(mobileViewRows);
    }, [props.headerItems, props.rows, props.mobileViewRows]);

    const getUnexpandedRows = () => {
        return props.rows?.slice(0, props.maxDefaultSize);
    };

    const getUnexpandedMobileViewRows = () => {
        return props.mobileViewRows?.slice(0, props.maxDefaultSize);
    };

    const isExpandable = () => {
        return props.maxDefaultSize && props.rows && props.rows.length > props.maxDefaultSize;
    };

    const getHeader = () => {
        if (!props.headerItems) {
            return null;
        }

        var headerStyle = styles.headerDefaultTheme;

        if (msTeamsContext.currentMsTeamsThemeName !== IThemeTeamsClient.Default) {
            headerStyle = styles.headerDarkTheme;
        }

        return {
            key: "header",
            items: props.headerItems,
            className: classNames(styles.header, headerStyle),
        };
    };

    const getShowHideSection = () => {
        if (!isExpandable()) {
            return null;
        }

        return (
            <Flex hAlign="center">
                <ShowMoreButton
                    titleHide={pl.common.hide}
                    titleShowMore={pl.common.showMore((props.rows?.length ?? 0) - (props.maxDefaultSize ?? 0))}
                    onClickExpand={() => {
                        setRows(props.rows);
                        setMobileViewRows(props.mobileViewRows);
                    }}
                    onClickHide={() => {
                        setRows(getUnexpandedRows());
                        setMobileViewRows(getUnexpandedMobileViewRows());
                    }}
                />
            </Flex>
        );
    };

    if (isMobile && props.mobileViewRows) {
        return (
            <>
                <Flex column>{mobileViewRows}</Flex>
                {getShowHideSection()}
            </>
        );
    }

    return (
        <>
            <Table className={styles.basicTable} rows={rows} header={getHeader()} accessibility={gridNestedBehavior} />
            {getShowHideSection()}
        </>
    );
};
